import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY } from './textFormatting';

// Color palette - Professional colors
const COLORS = {
  primary: [26, 54, 93],       // Dark blue
  secondary: [55, 65, 81],     // Gray-700
  success: [34, 139, 34],      // Forest green
  warning: [218, 165, 32],     // Golden rod
  danger: [178, 34, 34],       // Firebrick
  info: [65, 105, 225],        // Royal blue
  muted: [128, 128, 128],      // Gray
  lightBg: [249, 250, 251],    // Light gray bg
  white: [255, 255, 255],
  black: [33, 33, 33],
  border: [200, 200, 200]
};

// Get result color
const getResultColor = (result) => {
  const r = (result || '').toLowerCase();
  if (['green', 'verified', 'clear'].includes(r)) return COLORS.success;
  if (['amber', 'unable_to_verify', 'insufficiency'].includes(r)) return COLORS.warning;
  if (['red', 'discrepancy', 'failed'].includes(r)) return COLORS.danger;
  return COLORS.muted;
};

// Check type labels
const CHECK_TYPE_LABELS = {
  id_check: 'ID Verification',
  criminal_court_check: 'Criminal Court Check',
  court_advocate_check: 'Court Advocate Check',
  address_check: 'Address Verification',
  employment_check: 'Employment Verification',
  education_check: 'Education Verification',
  reference_check: 'Reference Check',
  credit_check: 'Credit Check',
  drug_test: 'Drug Test',
  world_criminal_db: 'World Database Check',
  india_criminal_db: 'India Criminal Database',
  personal_reference: 'Personal Reference',
  professional_reference: 'Professional Reference'
};

/**
 * Generate a comprehensive verification report PDF for a case
 * Professional Veecheck report format with improved readability
 */
export const generateVerificationReportPDF = async (options) => {
  const {
    reportType = 'final',
    caseData,
    employeeData,
    clientData,
    checksData = [],
    annexures = [],
    fieldPhotos = [],
    generatedBy = 'System'
  } = options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 0;
  
  const COMPANY_NAME = 'Veecheck';
  const COMPANY_TAGLINE = 'Trusted Background Verification Services';

  // Helper: Add confidential tag to page corner (subtle)
  const addConfidentialTag = () => {
    doc.setFillColor(220, 38, 38); // Red
    doc.roundedRect(pageWidth - 45, 5, 40, 10, 1, 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('CONFIDENTIAL', pageWidth - 25, 11, { align: 'center' });
  };

  // Helper: Add page header
  const addPageHeader = (title = '', showConfidential = true) => {
    // Header bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    // Company name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(COMPANY_NAME, 14, 12);
    
    // Page title
    if (title) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(title, 14, 20);
    }
    
    // Page number
    const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
    doc.setFontSize(8);
    doc.text(`Page ${currentPage}`, pageWidth - 14, 20, { align: 'right' });
    
    // Confidential tag (top right)
    if (showConfidential) {
      addConfidentialTag();
    }
    
    return 35;
  };

  // Helper: Add page footer
  const addPageFooter = () => {
    const footerY = pageHeight - 15;
    doc.setDrawColor(...COLORS.border);
    doc.line(14, footerY - 5, pageWidth - 14, footerY - 5);
    
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.muted);
    doc.text(COMPANY_NAME + ' | ' + COMPANY_TAGLINE, 14, footerY);
    doc.text(`Generated: ${formatDateDDMMYYYY(new Date())}`, pageWidth - 14, footerY, { align: 'right' });
  };

  // Helper: Check page break
  const checkPageBreak = (requiredSpace = 50) => {
    if (yPosition > pageHeight - requiredSpace - 20) {
      doc.addPage();
      yPosition = addPageHeader();
      return true;
    }
    return false;
  };

  // Helper: Draw section box
  const drawSectionBox = (y, height, title = null) => {
    doc.setFillColor(...COLORS.lightBg);
    doc.roundedRect(14, y, pageWidth - 28, height, 2, 2, 'F');
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(14, y, pageWidth - 28, height, 2, 2, 'S');
    
    if (title) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text(title, 20, y + 10);
      return y + 15;
    }
    return y + 8;
  };

  // Helper: Get result status
  const getResultStatus = (result) => {
    const r = (result || '').toLowerCase();
    if (['green', 'verified', 'clear'].includes(r)) return { text: 'CLEAR', color: COLORS.success };
    if (['amber', 'unable_to_verify', 'insufficiency'].includes(r)) return { text: 'UNABLE TO VERIFY', color: COLORS.warning };
    if (['red', 'discrepancy', 'failed'].includes(r)) return { text: 'DISCREPANCY', color: COLORS.danger };
    return { text: 'PENDING', color: COLORS.muted };
  };

  // Calculate overall status
  const greenChecks = checksData.filter(c => ['green', 'verified', 'clear'].includes((c.overall_result || '').toLowerCase()));
  const amberChecks = checksData.filter(c => ['amber', 'unable_to_verify'].includes((c.overall_result || '').toLowerCase()));
  const redChecks = checksData.filter(c => ['red', 'discrepancy', 'failed'].includes((c.overall_result || '').toLowerCase()));
  const pendingChecks = checksData.filter(c => !c.overall_result || c.overall_result === '' || c.overall_result === '-');
  
  let overallStatus = 'CLEAR';
  let overallColor = COLORS.success;
  if (redChecks.length > 0) {
    overallStatus = 'DISCREPANCY FOUND';
    overallColor = COLORS.danger;
  } else if (amberChecks.length > 0) {
    overallStatus = 'PARTIALLY VERIFIED';
    overallColor = COLORS.warning;
  } else if (pendingChecks.length > 0) {
    overallStatus = 'PENDING';
    overallColor = COLORS.muted;
  }

  // =====================================================
  // PAGE 1: COVER PAGE
  // =====================================================
  
  yPosition = addPageHeader('Background Verification Report');

  // Report Type Banner
  const reportTypeColor = reportType === 'final' ? COLORS.success : COLORS.info;
  doc.setFillColor(...reportTypeColor);
  doc.roundedRect(14, yPosition, pageWidth - 28, 12, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(reportType === 'final' ? 'FINAL VERIFICATION REPORT' : 'INTERIM VERIFICATION REPORT', pageWidth / 2, yPosition + 8, { align: 'center' });
  yPosition += 18;

  // Client & Reference Information
  const sectionStartY = drawSectionBox(yPosition, 35, 'Report Information');
  
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.black);
  
  const reportInfo = [
    ['Reference No:', caseData?.id || 'N/A', 'Client:', clientData?.company_name || 'N/A'],
    ['Report Date:', formatDateDDMMYYYY(new Date()), 'Entity:', clientData?.entity_name || employeeData?.entity_name || 'N/A'],
    ['Generated By:', generatedBy, 'Location:', employeeData?.present_address?.city || 'N/A']
  ];
  
  let infoY = sectionStartY;
  reportInfo.forEach(row => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.secondary);
    doc.text(row[0], 20, infoY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(String(row[1]).substring(0, 35), 50, infoY);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.secondary);
    doc.text(row[2], pageWidth / 2 + 5, infoY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(String(row[3]).substring(0, 35), pageWidth / 2 + 30, infoY);
    infoY += 7;
  });
  yPosition += 42;

  // Candidate Information
  const candStartY = drawSectionBox(yPosition, 40, 'Candidate Details');
  
  const candidateInfo = [
    ['Name:', employeeData?.name || 'N/A', "Father's Name:", employeeData?.father_name || 'N/A'],
    ['Date of Birth:', employeeData?.dob || 'N/A', 'Contact:', employeeData?.phone || 'N/A'],
    ['Email:', employeeData?.email || 'N/A', 'Category:', employeeData?.employee_category || employeeData?.category || 'N/A'],
    ['Employee ID:', employeeData?.employee_code || 'N/A', '', '']
  ];
  
  let candY = candStartY;
  candidateInfo.forEach(row => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.secondary);
    doc.text(row[0], 20, candY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(String(row[1]).substring(0, 40), 50, candY);
    
    if (row[2]) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.secondary);
      doc.text(row[2], pageWidth / 2 + 5, candY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.black);
      doc.text(String(row[3]).substring(0, 35), pageWidth / 2 + 40, candY);
    }
    candY += 7;
  });
  yPosition += 48;

  // Verification Timeline
  const timeStartY = drawSectionBox(yPosition, 22, 'Verification Timeline');
  
  const timelineInfo = [
    ['Initiated:', formatDateDDMMYYYY(caseData?.created_at) || 'N/A', 'Completed:', formatDateDDMMYYYY(caseData?.updated_at) || 'In Progress']
  ];
  
  let timeY = timeStartY;
  timelineInfo.forEach(row => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.secondary);
    doc.text(row[0], 20, timeY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(String(row[1]), 50, timeY);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.secondary);
    doc.text(row[2], pageWidth / 2 + 5, timeY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.black);
    doc.text(String(row[3]), pageWidth / 2 + 35, timeY);
    timeY += 7;
  });
  yPosition += 30;

  // Executive Summary Table
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Verification Summary', 14, yPosition);
  yPosition += 5;

  const summaryHeaders = [['Component', 'Status', 'Result', 'Remarks']];
  const summaryBody = checksData.map(check => {
    const checkType = typeof check.check_type === 'object' ? check.check_type.check_type : check.check_type;
    const checkLabel = CHECK_TYPE_LABELS[checkType] || checkType?.replace(/_/g, ' ');
    const resultStatus = getResultStatus(check.overall_result);
    const statusText = ['verified', 'failed', 'unable_to_verify'].includes(check.status) ? 'Completed' : 'In Progress';
    const remarks = check.verification_remarks || check.remarks || '-';
    
    return [checkLabel, statusText, resultStatus.text, remarks.substring(0, 30)];
  });

  autoTable(doc, {
    startY: yPosition,
    head: summaryHeaders,
    body: summaryBody,
    theme: 'striped',
    headStyles: { 
      fillColor: COLORS.primary, 
      textColor: [255, 255, 255], 
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 3
    },
    bodyStyles: { 
      fontSize: 8,
      cellPadding: 3,
      textColor: COLORS.black
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 25 },
      2: { cellWidth: 35 },
      3: { cellWidth: 'auto' }
    },
    didParseCell: function(data) {
      if (data.column.index === 2 && data.section === 'body') {
        const value = data.cell.raw;
        if (value === 'CLEAR') {
          data.cell.styles.textColor = COLORS.success;
          data.cell.styles.fontStyle = 'bold';
        } else if (value === 'UNABLE TO VERIFY') {
          data.cell.styles.textColor = COLORS.warning;
          data.cell.styles.fontStyle = 'bold';
        } else if (value === 'DISCREPANCY') {
          data.cell.styles.textColor = COLORS.danger;
          data.cell.styles.fontStyle = 'bold';
        } else if (value === 'PENDING') {
          data.cell.styles.textColor = COLORS.muted;
        }
      }
    },
    margin: { left: 14, right: 14 }
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // Overall Result Banner
  doc.setFillColor(...overallColor);
  doc.roundedRect(14, yPosition, pageWidth - 28, 18, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`OVERALL VERIFICATION STATUS: ${overallStatus}`, pageWidth / 2, yPosition + 11, { align: 'center' });

  yPosition += 25;

  // Color Legend
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.secondary);
  doc.text('Legend:', 14, yPosition);
  
  doc.setFillColor(...COLORS.success);
  doc.rect(35, yPosition - 3, 6, 6, 'F');
  doc.text('Clear', 43, yPosition);
  
  doc.setFillColor(...COLORS.warning);
  doc.rect(60, yPosition - 3, 6, 6, 'F');
  doc.text('Unable to Verify', 68, yPosition);
  
  doc.setFillColor(...COLORS.danger);
  doc.rect(105, yPosition - 3, 6, 6, 'F');
  doc.text('Discrepancy', 113, yPosition);
  
  doc.setFillColor(...COLORS.muted);
  doc.rect(140, yPosition - 3, 6, 6, 'F');
  doc.text('Pending', 148, yPosition);

  addPageFooter();

  // =====================================================
  // DETAILED VERIFICATION PAGES
  // =====================================================
  
  for (const check of checksData) {
    doc.addPage();
    
    const checkType = typeof check.check_type === 'object' ? check.check_type.check_type : check.check_type;
    const checkLabel = CHECK_TYPE_LABELS[checkType] || checkType?.replace(/_/g, ' ');
    const resultStatus = getResultStatus(check.overall_result);
    
    yPosition = addPageHeader(`${checkLabel}`);

    // Result banner
    doc.setFillColor(...resultStatus.color);
    doc.roundedRect(14, yPosition, pageWidth - 28, 12, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Verification Result: ${resultStatus.text}`, pageWidth / 2, yPosition + 8, { align: 'center' });
    yPosition += 18;

    // Check Details Box
    const detailStartY = drawSectionBox(yPosition, 25, 'Verification Details');
    
    doc.setFontSize(8);
    const checkDetails = [
      ['Initiated:', formatDateDDMMYYYY(check.initiated_at || check.created_at || caseData?.created_at) || 'N/A', 
       'Completed:', formatDateDDMMYYYY(check.completed_at || check.verified_at) || 'In Progress'],
      ['Assigned To:', check.assigned_to_name || check.assigned_to || 'N/A',
       'TAT:', check.tat_days ? `${check.tat_days} days` : 'N/A']
    ];
    
    let detailY = detailStartY;
    checkDetails.forEach(row => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.secondary);
      doc.text(row[0], 20, detailY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.black);
      doc.text(String(row[1]).substring(0, 40), 45, detailY);
      
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.secondary);
      doc.text(row[2], pageWidth / 2 + 5, detailY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.black);
      doc.text(String(row[3]).substring(0, 35), pageWidth / 2 + 35, detailY);
      detailY += 7;
    });
    yPosition += 32;

    // Verification Data Table based on check type
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('Verification Data', 14, yPosition);
    yPosition += 5;

    let verificationData = [];
    
    if (checkType === 'employment_check' && check.employment_verification) {
      const emp = check.employment_verification;
      verificationData = [
        ['Company Name', emp.company_name || 'N/A'],
        ['Designation', emp.designation || 'N/A'],
        ['Employment Period', `${emp.from_date || 'N/A'} to ${emp.to_date || 'Present'}`],
        ['HR Contact', emp.hr_email || emp.hr_phone || 'N/A'],
        ['Verification Method', emp.verification_method || 'N/A'],
        ['Remarks', emp.remarks || '-']
      ];
    } else if (checkType === 'education_check' && check.education_verification) {
      const edu = check.education_verification;
      verificationData = [
        ['Institution', edu.university_name || edu.institution_name || 'N/A'],
        ['Degree/Course', edu.degree || edu.course || 'N/A'],
        ['Year of Passing', edu.year_of_passing || 'N/A'],
        ['Roll No/Reg No', edu.roll_number || edu.registration_number || 'N/A'],
        ['Verification Method', edu.verification_method || 'N/A'],
        ['Remarks', edu.remarks || '-']
      ];
    } else if (checkType === 'address_check' && check.address_verification) {
      const addr = check.address_verification;
      verificationData = [
        ['Address Type', check.subcheck_type || 'Present Address'],
        ['Full Address', addr.complete_address || 'N/A'],
        ['City/State', `${addr.city || ''}, ${addr.state || ''}`],
        ['Pincode', addr.pincode || 'N/A'],
        ['Verification Method', addr.verification_method || 'Physical Visit'],
        ['Remarks', addr.remarks || '-']
      ];
    } else if (checkType === 'id_check') {
      verificationData = [
        ['Document Type', check.document_type || 'ID Document'],
        ['Document Number', check.document_number || 'N/A'],
        ['Verification Status', resultStatus.text],
        ['Remarks', check.remarks || '-']
      ];
    } else if (checkType === 'criminal_court_check' || checkType === 'court_advocate_check') {
      verificationData = [
        ['Court/District', check.court_name || 'N/A'],
        ['Search Scope', check.search_scope || 'Civil & Criminal'],
        ['Records Found', check.records_found ? 'Yes' : 'No'],
        ['Verification Status', resultStatus.text],
        ['Remarks', check.remarks || '-']
      ];
    } else if (checkType === 'reference_check' || checkType === 'personal_reference' || checkType === 'professional_reference') {
      const ref = check.reference_verification || {};
      verificationData = [
        ['Reference Name', ref.reference_name || 'N/A'],
        ['Relationship', ref.relationship || 'N/A'],
        ['Contact', ref.contact || 'N/A'],
        ['Feedback', ref.feedback?.substring(0, 50) || 'N/A'],
        ['Remarks', check.remarks || '-']
      ];
    } else if (checkType === 'drug_test') {
      verificationData = [
        ['Test Type', check.test_type || 'Standard Panel'],
        ['Sample Date', formatDateDDMMYYYY(check.sample_date) || 'N/A'],
        ['Lab Name', check.lab_name || 'N/A'],
        ['Result', resultStatus.text],
        ['Remarks', check.remarks || '-']
      ];
    } else {
      // Generic check data
      verificationData = [
        ['Check Type', checkLabel],
        ['Status', check.status || 'N/A'],
        ['Result', resultStatus.text],
        ['Remarks', check.remarks || check.verification_remarks || '-']
      ];
    }

    autoTable(doc, {
      startY: yPosition,
      body: verificationData,
      theme: 'plain',
      bodyStyles: { 
        fontSize: 8,
        cellPadding: 4,
        textColor: COLORS.black
      },
      columnStyles: {
        0: { cellWidth: 45, fontStyle: 'bold', textColor: COLORS.secondary },
        1: { cellWidth: 'auto' }
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      margin: { left: 14, right: 14 }
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // Verification Remarks Section
    if (check.verification_remarks || check.detailed_remarks) {
      checkPageBreak(40);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text('Detailed Remarks', 14, yPosition);
      yPosition += 5;
      
      doc.setFillColor(...COLORS.lightBg);
      doc.roundedRect(14, yPosition, pageWidth - 28, 30, 2, 2, 'F');
      doc.setDrawColor(...COLORS.border);
      doc.roundedRect(14, yPosition, pageWidth - 28, 30, 2, 2, 'S');
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.black);
      const remarks = check.verification_remarks || check.detailed_remarks || 'No additional remarks';
      const splitRemarks = doc.splitTextToSize(remarks, pageWidth - 40);
      doc.text(splitRemarks.slice(0, 4), 20, yPosition + 8);
      yPosition += 35;
    }

    // Annexures/Proof Documents Section
    const proofDocs = check.proof_documents || [];
    if (proofDocs.length > 0) {
      checkPageBreak(40);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.primary);
      doc.text('Annexures / Proof Documents', 14, yPosition);
      yPosition += 8;
      
      doc.setFillColor(236, 253, 245); // Light green
      doc.roundedRect(14, yPosition, pageWidth - 28, 8 + (proofDocs.length * 8), 2, 2, 'F');
      doc.setDrawColor(34, 197, 94); // Green border
      doc.roundedRect(14, yPosition, pageWidth - 28, 8 + (proofDocs.length * 8), 2, 2, 'S');
      
      let annexY = yPosition + 6;
      proofDocs.forEach((doc_item, idx) => {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(22, 163, 74); // Green text
        doc.text(`Annexure ${idx + 1}:`, 20, annexY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.black);
        doc.text(doc_item.name || doc_item.filename || `Document ${idx + 1}`, 52, annexY);
        if (doc_item.uploaded_at) {
          doc.setTextColor(107, 114, 128);
          doc.text(`(Uploaded: ${formatDateDDMMYYYY(doc_item.uploaded_at)})`, 120, annexY);
        }
        annexY += 8;
      });
      yPosition = annexY + 5;
    }

    addPageFooter();
  }

  // =====================================================
  // ANNEXURES PAGES - Full page for each proof document
  // =====================================================
  let allAnnexures = [];
  checksData.forEach((check, checkIdx) => {
    const checkType = typeof check.check_type === 'object' ? check.check_type.check_type : check.check_type;
    const checkLabel = CHECK_TYPE_LABELS[checkType] || checkType?.replace(/_/g, ' ');
    const proofDocs = check.proof_documents || [];
    proofDocs.forEach((docItem, docIdx) => {
      allAnnexures.push({
        checkLabel,
        checkIndex: checkIdx + 1,
        docIndex: docIdx + 1,
        ...docItem
      });
    });
  });

  if (allAnnexures.length > 0) {
    doc.addPage();
    yPosition = addPageHeader('Annexures Index');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('List of Supporting Documents', 14, yPosition);
    yPosition += 10;

    // Create annexure index table
    const annexureTableData = allAnnexures.map((ann, idx) => [
      `Annexure ${idx + 1}`,
      ann.checkLabel || 'N/A',
      ann.name || ann.filename || `Document ${idx + 1}`,
      ann.document_type || 'Proof Document',
      formatDateDDMMYYYY(ann.uploaded_at) || 'N/A'
    ]);

    doc.autoTable({
      startY: yPosition,
      head: [['#', 'Check Type', 'Document Name', 'Type', 'Uploaded']],
      body: annexureTableData,
      theme: 'grid',
      headStyles: {
        fillColor: COLORS.primary,
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 7,
        textColor: COLORS.black
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 35 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 30 },
        4: { cellWidth: 25 }
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251]
      },
      margin: { left: 14, right: 14 }
    });

    yPosition = doc.lastAutoTable.finalY + 10;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(107, 114, 128);
    doc.text('Note: Original documents are available in the digital case file for reference.', 14, yPosition);
    
    addPageFooter();
  }

  // =====================================================
  // DISCLAIMER PAGE
  // =====================================================
  doc.addPage();
  yPosition = addPageHeader('Disclaimer & Terms');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Important Disclaimer', 14, yPosition);
  yPosition += 8;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);

  const disclaimerPoints = [
    '1. This report has been prepared solely for the purpose of the engagement with the Client and is strictly confidential.',
    '2. The verification is based on information provided by the candidate and verification sources. Veecheck does not guarantee the absolute accuracy of all information.',
    '3. The contents of this report are strictly confidential and are intended solely for internal, non-commercial use by the Client.',
    '4. This report should not be shared with third parties without prior written consent from Veecheck.',
    '5. Veecheck shall not be liable for any decisions made based solely on this report.',
    '6. The verification results are valid as of the date of verification and may not reflect subsequent changes.',
    '7. Any discrepancies or concerns should be discussed with the candidate before making adverse decisions.',
    '8. This report does not constitute legal advice and should not be treated as such.'
  ];

  disclaimerPoints.forEach(point => {
    const splitText = doc.splitTextToSize(point, pageWidth - 32);
    splitText.forEach(line => {
      doc.text(line, 18, yPosition);
      yPosition += 5;
    });
    yPosition += 3;
  });

  yPosition += 10;

  // Authorization Section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Authorization', 14, yPosition);
  yPosition += 8;

  doc.setFillColor(...COLORS.lightBg);
  doc.roundedRect(14, yPosition, pageWidth - 28, 40, 2, 2, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(14, yPosition, pageWidth - 28, 40, 2, 2, 'S');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);
  doc.text('This report is certified to be accurate to the best of our knowledge and verification capabilities.', 20, yPosition + 10);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Authorized Signatory:', 20, yPosition + 22);
  doc.text('Date:', pageWidth / 2 + 10, yPosition + 22);
  
  doc.setFont('helvetica', 'normal');
  doc.text(generatedBy, 20, yPosition + 30);
  doc.text(formatDateDDMMYYYY(new Date()), pageWidth / 2 + 10, yPosition + 30);
  
  // Signature line
  doc.setDrawColor(...COLORS.border);
  doc.line(20, yPosition + 35, 80, yPosition + 35);
  doc.line(pageWidth / 2 + 10, yPosition + 35, pageWidth / 2 + 50, yPosition + 35);

  addPageFooter();

  // Save the PDF
  const fileName = `${reportType === 'final' ? 'FINAL' : 'INTERIM'}_REPORT_${caseData?.id || 'Case'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);

  return fileName;
};

/**
 * Generate a PDF dashboard summary report
 */
export const generateDashboardPDF = (options) => {
  const {
    title = 'Dashboard Report',
    subtitle = '',
    stats = [],
    tables = [],
    dateRange = 'All Time',
    generatedBy = 'System'
  } = options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 18);
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle || `Generated: ${new Date().toLocaleDateString()}`, 14, 30);
  
  doc.setFontSize(9);
  doc.text(`Date Range: ${dateRange}`, pageWidth - 14, 30, { align: 'right' });
  
  yPosition = 50;

  // Stats Grid (if provided)
  if (stats.length > 0) {
    const statWidth = (pageWidth - 28) / Math.min(stats.length, 4);
    
    stats.forEach((stat, index) => {
      const x = 14 + (index % 4) * statWidth;
      const y = yPosition + Math.floor(index / 4) * 30;
      
      doc.setFillColor(...(stat.color || COLORS.lightBg));
      doc.roundedRect(x, y, statWidth - 4, 25, 2, 2, 'F');
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.black);
      doc.text(String(stat.value), x + statWidth / 2 - 2, y + 12, { align: 'center' });
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.secondary);
      doc.text(stat.label, x + statWidth / 2 - 2, y + 20, { align: 'center' });
    });
    
    yPosition += Math.ceil(stats.length / 4) * 30 + 10;
  }

  // Tables
  tables.forEach(table => {
    if (yPosition > 240) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text(table.title, 14, yPosition);
    yPosition += 5;
    
    autoTable(doc, {
      startY: yPosition,
      head: [table.headers],
      body: table.rows,
      theme: 'striped',
      headStyles: { 
        fillColor: COLORS.primary, 
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold'
      },
      bodyStyles: { 
        fontSize: 8,
        textColor: COLORS.black
      },
      margin: { left: 14, right: 14 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 15;
  });

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.text(`Generated by: ${generatedBy}`, 14, doc.internal.pageSize.getHeight() - 10);
  }

  const fileName = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  
  return fileName;
};


/**
 * Generate Employment Verification Summary PDF
 */
export const generateEmploymentVerificationSummaryPDF = (options) => {
  const { employeeData, verificationData, candidateData, clientData } = options;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Employment Verification Summary', pageWidth / 2, 18, { align: 'center' });
  
  let y = 45;
  
  // Candidate Info
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Candidate: ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(candidateData?.name || employeeData?.name || 'N/A', 45, y);
  y += 15;
  
  // Employment Details Table
  const empData = verificationData || {};
  const tableData = [
    ['Company Name', empData.company_name || 'N/A'],
    ['Designation', empData.designation || 'N/A'],
    ['Employment Period', `${empData.from_date || 'N/A'} to ${empData.to_date || 'Present'}`],
    ['HR Email', empData.hr_email || 'N/A'],
    ['HR Phone', empData.hr_phone || 'N/A'],
    ['Verification Method', empData.verification_method || 'N/A'],
    ['Status', empData.status || 'Pending'],
    ['Result', empData.result || 'Pending'],
    ['Remarks', empData.remarks || '-']
  ];
  
  autoTable(doc, {
    startY: y,
    body: tableData,
    theme: 'striped',
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 }
  });
  
  doc.save(`Employment_Verification_${candidateData?.name || 'Summary'}.pdf`);
};

/**
 * Generate Education Verification Summary PDF
 */
export const generateEducationVerificationSummaryPDF = (options) => {
  const { employeeData, verificationData, candidateData, clientData } = options;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Education Verification Summary', pageWidth / 2, 18, { align: 'center' });
  
  let y = 45;
  
  // Candidate Info
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Candidate: ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(candidateData?.name || employeeData?.name || 'N/A', 45, y);
  y += 15;
  
  // Education Details Table
  const eduData = verificationData || {};
  const tableData = [
    ['Institution', eduData.university_name || eduData.institution_name || 'N/A'],
    ['Degree/Course', eduData.degree || eduData.course || 'N/A'],
    ['Year of Passing', eduData.year_of_passing || 'N/A'],
    ['Roll Number', eduData.roll_number || 'N/A'],
    ['Registration Number', eduData.registration_number || 'N/A'],
    ['Verification Method', eduData.verification_method || 'N/A'],
    ['Status', eduData.status || 'Pending'],
    ['Result', eduData.result || 'Pending'],
    ['Remarks', eduData.remarks || '-']
  ];
  
  autoTable(doc, {
    startY: y,
    body: tableData,
    theme: 'striped',
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 }
  });
  
  doc.save(`Education_Verification_${candidateData?.name || 'Summary'}.pdf`);
};

/**
 * Generate Address Verification Summary PDF
 */
export const generateAddressVerificationSummaryPDF = (options) => {
  const { employeeData, verificationData, candidateData, clientData, addressType } = options;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Address Verification Summary', pageWidth / 2, 18, { align: 'center' });
  
  let y = 45;
  
  // Candidate Info
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Candidate: ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(candidateData?.name || employeeData?.name || 'N/A', 45, y);
  y += 15;
  
  // Address Details Table
  const addrData = verificationData || {};
  const tableData = [
    ['Address Type', addressType || addrData.address_type || 'Present Address'],
    ['Full Address', addrData.complete_address || 'N/A'],
    ['City', addrData.city || 'N/A'],
    ['State', addrData.state || 'N/A'],
    ['Pincode', addrData.pincode || 'N/A'],
    ['Landmark', addrData.landmark || 'N/A'],
    ['Verification Method', addrData.verification_method || 'Physical Visit'],
    ['Status', addrData.status || 'Pending'],
    ['Result', addrData.result || 'Pending'],
    ['Remarks', addrData.remarks || '-']
  ];
  
  autoTable(doc, {
    startY: y,
    body: tableData,
    theme: 'striped',
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 }
  });
  
  doc.save(`Address_Verification_${candidateData?.name || 'Summary'}.pdf`);
};

/**
 * Generate Criminal Verification Summary PDF
 */
export const generateCriminalVerificationSummaryPDF = (options) => {
  const { employeeData, verificationData, candidateData, clientData } = options;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Criminal Court Verification Summary', pageWidth / 2, 18, { align: 'center' });
  
  let y = 45;
  
  // Candidate Info
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Candidate: ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(candidateData?.name || employeeData?.name || 'N/A', 45, y);
  y += 15;
  
  // Criminal Check Details Table
  const crimData = verificationData || {};
  const tableData = [
    ['Court/District', crimData.court_name || crimData.district || 'N/A'],
    ['Search Scope', crimData.search_scope || 'Civil & Criminal'],
    ['Records Found', crimData.records_found ? 'Yes' : 'No'],
    ['Case Details', crimData.case_details || 'N/A'],
    ['Verification Method', crimData.verification_method || 'N/A'],
    ['Status', crimData.status || 'Pending'],
    ['Result', crimData.result || 'Pending'],
    ['Remarks', crimData.remarks || '-']
  ];
  
  autoTable(doc, {
    startY: y,
    body: tableData,
    theme: 'striped',
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 }
  });
  
  doc.save(`Criminal_Verification_${candidateData?.name || 'Summary'}.pdf`);
};

/**
 * Generate ID Verification Summary PDF
 */
export const generateIDVerificationSummaryPDF = (options) => {
  const { employeeData, verificationData, candidateData, clientData } = options;
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('ID Verification Summary', pageWidth / 2, 18, { align: 'center' });
  
  let y = 45;
  
  // Candidate Info
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Candidate: ', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(candidateData?.name || employeeData?.name || 'N/A', 45, y);
  y += 15;
  
  // ID Check Details Table
  const idData = verificationData || {};
  const tableData = [
    ['Document Type', idData.document_type || 'ID Document'],
    ['Document Number', idData.document_number || 'N/A'],
    ['Name on Document', idData.name_on_document || 'N/A'],
    ['Issue Date', idData.issue_date || 'N/A'],
    ['Expiry Date', idData.expiry_date || 'N/A'],
    ['Verification Method', idData.verification_method || 'N/A'],
    ['Status', idData.status || 'Pending'],
    ['Result', idData.result || 'Pending'],
    ['Remarks', idData.remarks || '-']
  ];
  
  autoTable(doc, {
    startY: y,
    body: tableData,
    theme: 'striped',
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold' },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 14, right: 14 }
  });
  
  doc.save(`ID_Verification_${candidateData?.name || 'Summary'}.pdf`);
};

